import { z } from 'zod';
import { supabase } from '@/utils/supabase';

const cents = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).nullable();
const isoTime = z.string().datetime({ offset: true });
const associationStatus = z.enum(['matched', 'waiting', 'review']).nullable().optional();
const feeSource = z.enum(['CD', 'LE']).nullable().optional();
const findingSchema = z.object({
  id: z.string().uuid(),
  attachmentId: z.string().uuid().optional(),
  pending: z.boolean().optional(),
  freshState: z.enum(['none', 'pending', 'satisfied', 'attention']).optional(),
  retrying: z.boolean().optional(),
  needsAttention: z.boolean().optional(),
  freshRequestedAt: isoTime.nullable().optional(),
  freshExtractionSkipped: z.boolean().optional(),
  extractedAt: isoTime.nullable().optional(),
  associationStatus,
  associationReason: z.string().nullable().optional(),
  identityNote: z.string().nullable().optional(),
  renameState: z.string().optional(),
  loanId: z.string().uuid().optional(),
  encompassLoanId: z.string().optional(),
  reportId: z.string().uuid().nullable().optional(),
  appraisalReportId: z.string().uuid().nullable().optional(),
  orderId: z.string().uuid().nullable().optional(),
  orderNumber: z.string().nullable().optional(),
  cures: z.object({ requiredCents: cents, appliedCents: cents, checkedAt: isoTime.nullable().optional() }).nullable().optional(),
  feeField: z.string().nullable().optional(),
  feeSource,
  ruleVersion: z.string().optional(),
  alertsStoppedAt: isoTime.nullable().optional(),
  alertsStoppedBy: z.string().nullable().optional(),
  alertsStoppedReason: z.string().nullable().optional(),
  loanNumber: z.string(),
  borrowerName: z.string().nullable(),
  documentTitle: z.string(),
  invoiceFolder: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  vendor: z.string().nullable(),
  outcome: z.enum(['overage', 'review', 'waiting']),
  reason: z.string().nullable(),
  reasonCode: z.string().nullable().optional(),
  currentFeeCents: cents.optional(),
  lastDisclosedLECents: cents.optional(),
  lastDisclosedCDCents: cents.optional(),
  invoiceCents: cents,
  invoiceAmountStatus: z.enum(['verified', 'extracted', 'unavailable']).optional(),
  feeLine: z.string().nullable(),
  feeCents: cents,
  excessCents: cents,
  checkedAt: isoTime.nullable().optional(),
  evaluatedAt: isoTime
}).refine(row => row.outcome !== 'overage' || (
  row.invoiceCents !== null && row.feeCents !== null && row.excessCents !== null
  && row.invoiceAmountStatus !== 'extracted' && row.invoiceAmountStatus !== 'unavailable'
  && row.excessCents >= 1 && row.invoiceCents - row.feeCents === row.excessCents
), 'A confirmed overage requires verified, consistent amounts')
  .refine(row => row.outcome !== 'waiting' || row.reasonCode === 'awaiting_disclosure', 'Only invoices awaiting disclosure belong in this report');
const reportSchema = z.object({
  rows: z.array(findingSchema).max(500),
  truncated: z.boolean()
});

export type AppraisalFeeFinding = z.infer<typeof findingSchema>;
export type AppraisalFeeComplianceReport = z.infer<typeof reportSchema>;

/** One saved comparison version. Display fields only; evidence stays service-only. */
const historyEntrySchema = z.object({
  id: z.string().uuid(),
  attachmentId: z.string().uuid(),
  documentTitle: z.string(),
  invoiceFolder: z.string().nullable(),
  attachmentActive: z.boolean(),
  attachmentExtractedAt: isoTime.nullable(),
  createdAt: isoTime,
  evaluatedAt: isoTime.nullable(),
  checkedAt: isoTime.nullable(),
  supersededAt: isoTime.nullable(),
  current: z.boolean(),
  outcome: z.enum(['waiting', 'passed', 'overage', 'review', 'excluded', 'duplicate', 'superseded']),
  reasonCode: z.string().nullable(),
  reason: z.string().nullable(),
  invoiceCents: cents,
  invoiceAmountStatus: z.enum(['verified', 'extracted', 'unavailable']),
  feeCents: cents,
  feeSource,
  excessCents: cents,
  ruleVersion: z.string(),
  readerVersion: z.string().nullable(),
  notes: z.array(z.string().max(500)).max(40).optional(),
  vendor: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  orderNumber: z.string().nullable(),
  cures: z.object({ requiredCents: cents, appliedCents: cents }).nullable().optional(),
  associationStatus,
  associationReason: z.string().nullable().optional(),
  identityNote: z.string().nullable().optional(),
  renameState: z.string(),
  renameTitle: z.string().nullable(),
  freshRequestedAt: isoTime.nullable(),
  freshExtractionSkipped: z.boolean().optional()
});
export type AppraisalFeeHistoryEntry = z.infer<typeof historyEntrySchema>;

export async function getAppraisalFeeComplianceReport(): Promise<AppraisalFeeComplianceReport> {
  const { data, error } = await supabase.rpc('get_appraisal_fee_compliance_report');
  if (error) {
    throw new Error(error.code === '42501'
      ? 'You do not have access to this report.'
      : 'Fee compliance findings could not be loaded.');
  }
  const result = reportSchema.safeParse(data);
  if (!result.success) {
    throw new Error('Fee compliance findings could not be read. Please try again.');
  }
  return result.data;
}

const stopResultSchema = z.object({ alertsStoppedAt: isoTime, alertsStoppedBy: z.string().nullable(), alertsStoppedReason: z.string() });
export type AppraisalFeeAlertStop = z.infer<typeof stopResultSchema>;
const STOP_ERRORS: Record<string, string> = {
  fee_alert_reason_invalid: 'Enter a reason between 10 and 500 characters.',
  fee_alerts_already_stopped: 'Alerts are already stopped for this loan. Refresh to see who stopped them.',
  fee_alerts_not_stopped: 'Alerts are not stopped for this loan.'
};
function alertError(error: { code?: string; message?: string }, fallback: string): Error {
  if (error.code === '42501') {
    return new Error('You do not have access to change alerts for this loan.');
  }
  return new Error(STOP_ERRORS[error.message || ''] || fallback);
}

/** Stops overage emails and the digest for one loan. The finding stays visible; the reason is saved with the user and time. */
export async function stopAppraisalFeeAlerts(loanId: string, reason: string): Promise<AppraisalFeeAlertStop> {
  const { data, error } = await supabase.rpc('fee_verification_stop_alerts', { p_loan_id: loanId, p_reason: reason });
  if (error) {
    throw alertError(error, 'Alerts could not be stopped. Please try again.');
  }
  const result = stopResultSchema.safeParse(data);
  if (!result.success) {
    throw new Error('Alerts could not be stopped. Please try again.');
  }
  return result.data;
}

/** Re-enables future overage emails and the digest for the loan. Findings already suppressed are not re-sent. */
export async function resumeAppraisalFeeAlerts(loanId: string): Promise<void> {
  const { error } = await supabase.rpc('fee_verification_resume_alerts', { p_loan_id: loanId });
  if (error) {
    throw alertError(error, 'Alerts could not be resumed. Please try again.');
  }
}

/** Every saved audit version for the loan's appraisal invoices, newest first, after the loan-access check. */
export async function getAppraisalFeeHistory(loanId: string): Promise<AppraisalFeeHistoryEntry[]> {
  const { data, error } = await supabase.rpc('get_appraisal_fee_history', { p_loan_id: loanId });
  if (error) {
    throw new Error(error.code === '42501'
      ? 'You do not have access to this loan.'
      : 'Audit history could not be loaded.');
  }
  const result = z.array(historyEntrySchema).max(200).safeParse(data);
  if (!result.success) {
    throw new Error('Audit history could not be read. Please try again.');
  }
  return result.data;
}
